<template>
  <div class="row d-flex justify-content-center">
    <div class="col-12 col-md-6">
      <div class="card StripeCard">
        <div class="card-body">
          <h3 class="fw-bold">
            {{ 'Enter your card details' | translate }}.
            <br />
            {{ 'Your subscription will start now' | translate }}.
          </h3>
          <p>
            → {{ 'You will be charged at the end of billing period' | translate }}
          </p>
          <div class="mb-3">
            <input
              id="card-holder-name"
              v-model="cardHolderName"
              class="form-control"
              type="text"
              :placeholder="$t('First and last name on card')"
            />
          </div>

          <div class="mb-3">
            <!-- Stripe Elements Placeholder -->
            <div id="card-element" ref="card-element"></div>
            <div class="text-danger">{{ cardErrors }}</div>
          </div>
          <div class="text-center">
            <button v-if="pending" class="btn btn-primary" disabled>
              <span
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {{ 'In progress...' | translate }}
            </button>
            <button
              id="card-button"
              class="btn btn-primary"
              @click="handleSubmit"
              v-else
            >
              {{ 'Subscribe' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['stripeKey', 'plan', 'currency'],
  data() {
    return {
      cardHolderName: '',
      stripe: Stripe(this.stripeKey, {
        locale: document.documentElement.lang.substr(0, 2),
      }),
      elements: null,
      cardElement: null,
      cardErrors: '',
      pending: false,
      style: {
        // padding: '10px 12px',
        color: '#495057',
        fontFamily: '"Lato", sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '0.85rem',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
    }
  },
  mounted() {
    this.elements = this.stripe.elements()

    this.cardElement = this.elements.create('card', {
      style: this.style,
      hidePostalCode: true,
      classes: {
        base: 'form-control',
      },
    })

    this.cardElement.mount(this.$refs['card-element'])
    this.cardElement.on('change', this.showCardError)
  },
  methods: {
    showCardError(event) {
      if (event.error) {
        this.cardErrors = event.error.message
      } else {
        this.cardErrors = ''
      }
    },
    handleSubmit() {
      this.pending = true
      this.stripe
        .createPaymentMethod({
          type: 'card',
          card: this.cardElement,
        })
        .then((response) => {
          if (response.error) {
            throw response
          } else {
            // this.createSubscription(
            //   response.paymentMethod.id,
            //   this.plan.priceId
            // )
            this.createSetupIntent(
              response.paymentMethod.id,
              this.plan.priceId
            )
          }
        })
        .catch(this.displayError)
    },
    createSetupIntent(paymentMethodId, priceId) {
      axios
        .post('/account/subscription/setup-intent', {
          paymentMethodId,
          priceId,
          plan: this.plan,
        })
        .then((response) => {
          if (response.data.error) {
            throw response.data
          }
          return {
            setupIntent: response.data,
            paymentMethodId,
            priceId,
          }
        })
        .then(this.handleCustomerActionRequiredForSetupIntent)
        .then(this.createSubscription)
        .then((response) => {
          if (response.data.error) {
            throw response.data
          }
          return {
            subscription: response.data,
            paymentMethodId,
            priceId,
          }
        })
        .then(this.handleCustomerActionRequired)
        .then(this.handlePaymentMethodRequired)
        .then(this.onSubscriptionComplete)
        .catch(this.displayError)
    },
    createSubscription({ paymentMethodId, priceId, setupIntent }) {
      return axios.post('/account/subscription', {
        paymentMethodId,
        priceId,
        plan: this.plan,
      })
    },
    displayError(event) {
      console.error(event)
      this.pending = false
      if (event.error) {
        this.cardErrors = event.error.message
      } else {
        this.cardErrors = ''
      }
    },
    handlePaymentMethodRequired({ subscription, paymentMethodId, priceId }) {
      if (subscription.status === 'active') {
        // subscription is active, no customer actions required.
        return { subscription, priceId, paymentMethodId }
      } else if (
        subscription.latest_invoice.payment_intent.status ===
        'requires_payment_method'
      ) {
        // Using localStorage to store the state of the retry here
        // (feel free to replace with what you prefer)
        // Store the latest invoice ID and status
        // localStorage.setItem('latestInvoiceId', subscription.latest_invoice.id)
        // localStorage.setItem(
        //   'latestInvoicePaymentIntentStatus',
        //   subscription.latest_invoice.payment_intent.status
        // )
        this.pending = false
        throw { error: { message: 'Your card was declined.' } }
      } else {
        return { subscription, priceId, paymentMethodId }
      }
    },
    handleCustomerActionRequiredForSetupIntent({
      setupIntent,
      priceId,
      paymentMethodId,
    }) {
      if (setupIntent.status === 'requires_action') {
        return this.stripe
          .confirmCardSetup(setupIntent.client_secret, {
            payment_method: paymentMethodId,
          })
          .then((result) => {
            if (result.error) {
              // start code flow to handle updating the payment details
              // Display error message in your UI.
              // The card was declined (i.e. insufficient funds, card has expired, etc)
              throw result
            } else {
              if (result.setupIntent.status === 'succeeded') {
                // There's a risk of the customer closing the window before callback
                // execution. To handle this case, set up a webhook endpoint and
                // listen to invoice.paid. This webhook endpoint returns an Invoice.

                return {
                  setupIntent: result.setupIntent,
                  priceId: priceId,
                  paymentMethodId: paymentMethodId,
                }
              }
            }
          })
      } else {
        // No customer action needed
        return { setupIntent, priceId, paymentMethodId }
      }
    },
    handleCustomerActionRequired({
      subscription,
      invoice,
      priceId,
      paymentMethodId,
      isRetry,
    }) {
      if (subscription && subscription.status === 'active') {
        // subscription is active, no customer actions required.
        return { subscription, priceId, paymentMethodId }
      }

      // If it's a first payment attempt, the payment intent is on the subscription latest invoice.
      // If it's a retry, the payment intent will be on the invoice itself.

      let paymentIntent = invoice
        ? invoice.payment_intent
        : subscription.latest_invoice.payment_intent

      if (
        paymentIntent.status === 'requires_action' ||
        (isRetry === true && paymentIntent.status === 'requires_payment_method')
      ) {
        return this.stripe
          .confirmCardPayment(paymentIntent.client_secret, {
            payment_method: paymentMethodId,
          })
          .then((result) => {
            if (result.error) {
              // start code flow to handle updating the payment details
              // Display error message in your UI.
              // The card was declined (i.e. insufficient funds, card has expired, etc)
              throw result
            } else {
              if (result.paymentIntent.status === 'succeeded') {
                // There's a risk of the customer closing the window before callback
                // execution. To handle this case, set up a webhook endpoint and
                // listen to invoice.paid. This webhook endpoint returns an Invoice.
                subscription.status = 'active'
                return {
                  priceId: priceId,
                  subscription: subscription,
                  invoice: invoice,
                  paymentMethodId: paymentMethodId,
                }
              }
            }
          })
      } else {
        // No customer action needed
        return { subscription, priceId, paymentMethodId }
      }
    },
    onSubscriptionComplete(result) {
      this.pending = false
      this.$emit('subscriptionCreated', result)
      // Payment was successful. Provision access to your service.
      // Remove invoice from localstorage because payment is now complete.

      // clearCache()

      // Change your UI to show a success message to your customer.

      // onSubscriptionSampleDemoComplete(result)

      // Call your backend to grant access to your service based on
      // the product your customer subscribed to.
      // Get the product by using result.subscription.price.product
    },
  },
}
</script>
